import React, { useState } from "react";

import { Briefcase, Loader, Lock, Mail, Phone, User } from "lucide-react";

import { api } from "../utils/apiClient";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/Dialog";

const AuthForm = ({ onAuthSuccess, onCancel, initialMode = "login" }) => {
  const [isLogin, setIsLogin] = useState(initialMode === "login");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [loginFormData, setLoginFormData] = useState({
    email: "",
    password: "",
  });

  const [signupFormData, setSignupFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    companyName: "",
    phoneNumber: "",
  });

  const [agreedToPolicies, setAgreedToPolicies] = useState(false);

  React.useEffect(() => {
    setIsLogin(initialMode === "login");
  }, [initialMode]);

  const handleLoginChange = (e) => {
    setLoginFormData({ ...loginFormData, [e.target.name]: e.target.value });
  };

  const handleSignupChange = (e) => {
    setSignupFormData({ ...signupFormData, [e.target.name]: e.target.value });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.post(
        "/login",
        new URLSearchParams({
          username: loginFormData.email,
          password: loginFormData.password,
        }).toString(),
        true,
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        },
      );

      const data = response.data;
      localStorage.setItem("accessToken", data.access_token);
      onAuthSuccess();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || "Login failed. Please check your credentials.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!agreedToPolicies) {
      setError("Please accept the Privacy Policy and Terms and Conditions to continue.");
      return;
    }
    if (signupFormData.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setIsLoading(true);
    try {
      const signupResponse = await api.post(
        "/users/signup",
        {
          email: signupFormData.email,
          password: signupFormData.password,
          full_name: signupFormData.fullName,
          company_name: signupFormData.companyName,
          phone_number: signupFormData.phoneNumber,
        },
        true,
      );

      localStorage.setItem("accessToken", signupResponse.data.access_token);
      setSignupFormData({
        email: "",
        password: "",
        fullName: "",
        companyName: "",
        phoneNumber: "",
      });
      setAgreedToPolicies(false);
      onAuthSuccess();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Signup failed. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForms = () => {
    setError(null);
    setLoginFormData({ email: "", password: "" });
    setSignupFormData({
      email: "",
      password: "",
      fullName: "",
      companyName: "",
      phoneNumber: "",
    });
    setAgreedToPolicies(false);
  };

  const inputClasses =
    "w-full bg-surface-container-low border-none rounded-xl pl-12 pr-5 py-3.5 text-on-surface text-sm placeholder:text-outline/50 focus:ring-2 focus:ring-primary/20 focus:bg-surface-container-lowest transition-all outline-none";

  const iconClasses = "absolute left-4 top-1/2 -translate-y-1/2 text-primary/60 w-5 h-5";

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isLogin ? "Welcome Back" : "Create Your Account"}</DialogTitle>
          <DialogDescription>
            {isLogin
              ? "Log in to access your audit reports and dashboard."
              : "Get started with AppearInAISearch in seconds."}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {error && (
            <div className="bg-error-container text-on-error-container p-3.5 rounded-xl mb-4 text-center text-sm font-medium">
              {error}
            </div>
          )}

          {isLogin ? (
            <form onSubmit={handleLoginSubmit} className="space-y-3">
              <div className="relative">
                <Mail className={iconClasses} />
                <input
                  type="email"
                  name="email"
                  value={loginFormData.email}
                  onChange={handleLoginChange}
                  placeholder="Email Address"
                  required
                  className={inputClasses}
                />
              </div>
              <div className="relative">
                <Lock className={iconClasses} />
                <input
                  type="password"
                  name="password"
                  value={loginFormData.password}
                  onChange={handleLoginChange}
                  placeholder="Password"
                  required
                  className={inputClasses}
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-3.5 rounded-xl font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2.5 disabled:opacity-60 mt-2"
              >
                {isLoading ? (
                  <>
                    <Loader size={18} className="animate-spin" /> <span>Logging in...</span>
                  </>
                ) : (
                  "Log In"
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSignupSubmit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative">
                  <Mail className={iconClasses} />
                  <input
                    type="email"
                    name="email"
                    value={signupFormData.email}
                    onChange={handleSignupChange}
                    placeholder="Email Address"
                    required
                    className={inputClasses}
                  />
                </div>
                <div className="relative">
                  <Lock className={iconClasses} />
                  <input
                    type="password"
                    name="password"
                    value={signupFormData.password}
                    onChange={handleSignupChange}
                    placeholder="Password"
                    required
                    className={inputClasses}
                  />
                </div>
              </div>
              <div className="relative">
                <User className={iconClasses} />
                <input
                  type="text"
                  name="fullName"
                  value={signupFormData.fullName}
                  onChange={handleSignupChange}
                  placeholder="Full Name"
                  required
                  className={inputClasses}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative">
                  <Briefcase className={iconClasses} />
                  <input
                    type="text"
                    name="companyName"
                    value={signupFormData.companyName}
                    onChange={handleSignupChange}
                    placeholder="Company Name"
                    required
                    className={inputClasses}
                  />
                </div>
                <div className="relative">
                  <Phone className={iconClasses} />
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={signupFormData.phoneNumber}
                    onChange={handleSignupChange}
                    placeholder="Phone Number"
                    required
                    className={inputClasses}
                  />
                </div>
              </div>
              {/* Opened in a new tab so signing up does not discard a part-filled form. */}
              <label className="flex items-start gap-3 pt-1 text-sm text-on-surface-variant cursor-pointer">
                <input
                  type="checkbox"
                  name="agreedToPolicies"
                  checked={agreedToPolicies}
                  onChange={(e) => setAgreedToPolicies(e.target.checked)}
                  required
                  className="mt-0.5 w-4 h-4 shrink-0 rounded accent-primary cursor-pointer"
                />
                <span>
                  I Agree to the{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary font-semibold hover:underline"
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary font-semibold hover:underline"
                  >
                    Terms and Conditions
                  </a>
                </span>
              </label>
              <button
                type="submit"
                disabled={isLoading || !agreedToPolicies}
                className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-3.5 rounded-xl font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2.5 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
              >
                {isLoading ? (
                  <>
                    <Loader size={18} className="animate-spin" /> <span>Creating account...</span>
                  </>
                ) : (
                  "Create Account"
                )}
              </button>
            </form>
          )}

          <p className="mt-5 text-center text-sm text-on-surface-variant">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                resetForms();
              }}
              className="text-primary hover:underline ml-1.5 font-semibold transition-colors"
            >
              {isLogin ? "Sign Up" : "Log In"}
            </button>
          </p>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default AuthForm;
